import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, X, Loader2, Check, Pencil, Play } from 'lucide-react';
import { compressImage } from '../utils/imageCompress';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminAuth = () => {
  try { return JSON.parse(sessionStorage.getItem('gw_admin_auth') || 'null'); } catch { return null; }
};

const ytId = (url = '') => {
  const m = url.match(/(?:youtu\.be\/|v=|embed\/)([A-Za-z0-9_-]{6,})/);
  return m ? m[1] : '';
};
const movUrl = (id) => `${API}/vip/movements/${id}/file`;

const EMPTY_FORM = { title: '', description: '', modifications: '', video_url: '', file: null };

export default function MyMovements({ openId, onOpened }) {
  const { user } = useAuth();
  const admin = getAdminAuth();
  const [movements, setMovements] = useState([]);
  const [picked, setPicked] = useState([]);
  const [active, setActive] = useState(null);     // movement being viewed
  const [showForm, setShowForm] = useState(false); // add/edit form
  const [editing, setEditing] = useState(null);    // movement being edited
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/vip/movements`);
      if (res.ok) setMovements((await res.json()).movements || []);
    } catch { /* ignore */ }
  }, []);

  const loadPicks = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await fetch(`${API}/vip/movement-picks?user_id=${encodeURIComponent(user.id)}`);
      if (res.ok) setPicked((await res.json()).picked || []);
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadPicks(); }, [loadPicks]);

  // Open a movement requested from the VIP search
  useEffect(() => {
    const m = openId && movements.find((x) => x.id === openId);
    if (m) { setActive(m); onOpened?.(); }
  }, [openId, movements, onOpened]);

  useEffect(() => {
    document.body.style.overflow = (active || showForm) ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [active, showForm]);

  const togglePick = async (mid) => {
    if (!user?.id) return;
    const isPicked = picked.includes(mid);
    setPicked((prev) => isPicked ? prev.filter((x) => x !== mid) : [...prev, mid]); // optimistic
    const fd = new FormData();
    fd.append('user_id', user.id);
    fd.append('movement_id', mid);
    fd.append('picked', (!isPicked).toString());
    try {
      await fetch(`${API}/vip/movement-picks`, { method: 'POST', body: fd });
    } catch {
      loadPicks(); // revert on error
    }
  };

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setShowForm(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({ title: m.title, description: m.description || '', modifications: m.modifications || '', video_url: m.video_url || '', file: null });
    setShowForm(true);
  };

  const submitForm = async (e) => {
    e.preventDefault();
    if (!admin || !form.title.trim()) return;
    if (!editing && !form.file) { alert('Please choose a photo.'); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('description', form.description);
      fd.append('modifications', form.modifications);
      fd.append('video_url', form.video_url);
      const q = `admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`;
      let res;
      if (editing) {
        res = await fetch(`${API}/vip/movements/${editing.id}?${q}`, { method: 'PUT', body: fd });
      } else {
        const toSend = await compressImage(form.file);
        fd.append('file', toSend);
        res = await fetch(`${API}/vip/movements?${q}`, { method: 'POST', body: fd });
      }
      if (res.ok) {
        setShowForm(false); setForm(EMPTY_FORM); setEditing(null);
        await load();
      } else {
        alert(res.status === 413 ? 'That photo is too large.' : 'Save failed — please try again.');
      }
    } catch {
      alert('Save failed — please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const deleteMovement = async (mid) => {
    if (!admin) return;
    if (!window.confirm('Remove this movement?')) return;
    const res = await fetch(`${API}/vip/movements/${mid}?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`, { method: 'DELETE' });
    if (res.ok) { setActive(null); await load(); }
  };

  const activeVid = active?.video_url ? ytId(active.video_url) : '';

  return (
    <section className="mb-12" data-testid="my-movements">
      <div className="rounded-2xl border border-stone-200 bg-[#e4efe8] p-6 md:p-7 mb-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-heading text-2xl md:text-3xl text-forest">My Movements</h2>
            <p className="text-stone-600 font-body italic mt-1">Try each one — tick the stretches that feel good for you, and they'll be your keepers.</p>
          </div>
          {admin && (
            <button onClick={openAdd} className="inline-flex items-center gap-2 bg-forest text-cream font-body text-sm px-4 py-2 rounded-full hover:bg-forest-dark transition-colors flex-shrink-0" data-testid="movement-add-btn">
              <Plus size={15} /> Add a movement
            </button>
          )}
        </div>
      </div>

      {movements.length === 0 ? (
        <div className="bg-white border border-stone-200 rounded-2xl p-10 text-center" data-testid="movements-empty">
          <p className="font-body text-stone-500">{admin ? 'No movements yet — add your first stretch above.' : 'Movements coming soon.'}</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-7 xl:grid-cols-8 gap-3" data-testid="movements-grid">
          {movements.map((m) => {
            const isPicked = picked.includes(m.id);
            return (
              <div key={m.id} className="relative group" data-testid={`movement-${m.id}`}>
                <button
                  type="button"
                  onClick={() => setActive(m)}
                  className={`block w-full text-left bg-white rounded-xl border overflow-hidden transition-all hover:shadow-md ${isPicked ? 'border-forest ring-2 ring-forest/40' : 'border-stone-200'}`}
                  data-testid={`movement-open-${m.id}`}
                >
                  <div className="aspect-square bg-stone-100 overflow-hidden">
                    <img src={movUrl(m.id)} alt={m.title} loading="lazy" className="w-full h-full object-cover" />
                  </div>
                  <p className="font-body text-[11px] leading-tight text-stone-700 px-1.5 py-1.5 text-center line-clamp-2">{m.title}</p>
                </button>
                {/* client tick */}
                <button
                  type="button"
                  onClick={() => togglePick(m.id)}
                  title={isPicked ? 'One of your keepers' : 'Tick if this is good for you'}
                  className={`absolute top-1.5 right-1.5 w-6 h-6 rounded-md flex items-center justify-center transition-colors shadow-sm ${isPicked ? 'bg-forest text-cream' : 'bg-white/90 text-stone-400 hover:text-forest border border-stone-300'}`}
                  data-testid={`movement-pick-${m.id}`}
                >
                  {isPicked ? <Check size={14} /> : <Check size={14} className="opacity-30" />}
                </button>
                {m.video_url && (
                  <span className="absolute top-1.5 left-1.5 bg-cream/95 text-forest rounded-full p-1 shadow-sm pointer-events-none">
                    <Play size={10} />
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-forest/80 backdrop-blur-sm flex items-start justify-center p-3 sm:p-6 overflow-y-auto"
            onClick={() => setActive(null)}
            data-testid="movement-detail-modal"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              className="bg-cream rounded-2xl w-full max-w-2xl my-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-stone-200 sticky top-0 bg-cream rounded-t-2xl z-10">
                <h3 className="font-heading text-xl text-forest">{active.title}</h3>
                <div className="flex items-center gap-3">
                  {admin && (
                    <>
                      <button onClick={() => openEdit(active)} className="text-stone-400 hover:text-forest" title="Edit" data-testid={`movement-edit-${active.id}`}><Pencil size={17} /></button>
                      <button onClick={() => deleteMovement(active.id)} className="text-stone-400 hover:text-red-600" title="Remove" data-testid={`movement-delete-${active.id}`}><Trash2 size={17} /></button>
                    </>
                  )}
                  <button onClick={() => setActive(null)} className="text-stone-400 hover:text-forest" data-testid="movement-detail-close"><X size={22} /></button>
                </div>
              </div>
              <div className="p-5 space-y-4">
                <img src={movUrl(active.id)} alt={active.title} className="w-full max-h-[60vh] object-contain rounded-xl bg-stone-50" />
                {user?.id && (
                  <button
                    onClick={() => togglePick(active.id)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full font-body text-sm transition-colors ${picked.includes(active.id) ? 'bg-forest text-cream' : 'bg-forest/5 border border-forest/30 text-forest hover:bg-forest/10'}`}
                    data-testid={`movement-detail-pick-${active.id}`}
                  >
                    <Check size={15} /> {picked.includes(active.id) ? "This is one of my keepers" : "This feels good for me"}
                  </button>
                )}
                {active.description && (
                  <div>
                    <p className="font-heading text-sm text-forest mb-1">How to</p>
                    <p className="font-body text-sm text-stone-600 leading-relaxed whitespace-pre-line">{active.description}</p>
                  </div>
                )}
                {active.modifications && (
                  <div>
                    <p className="font-heading text-sm text-forest mb-1">Modifications</p>
                    <p className="font-body text-sm text-stone-600 leading-relaxed whitespace-pre-line">{active.modifications}</p>
                  </div>
                )}
                {activeVid && (
                  <div className="aspect-video rounded-xl overflow-hidden bg-black">
                    <iframe
                      title={active.title}
                      src={`https://www.youtube.com/embed/${activeVid}?rel=0&playsinline=1`}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      allowFullScreen
                    />
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Add / edit form */}
      <AnimatePresence>
        {showForm && admin && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => !saving && setShowForm(false)}
            data-testid="movement-form-modal"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md p-6 my-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-xl text-forest">{editing ? 'Edit movement' : 'Add a movement'}</h3>
                <button onClick={() => !saving && setShowForm(false)} className="text-stone-400 hover:text-forest"><X size={20} /></button>
              </div>
              <form onSubmit={submitForm} className="space-y-4">
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Name *</label>
                  <input type="text" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest" data-testid="movement-title" />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Description</label>
                  <textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest resize-none" data-testid="movement-description" />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Modifications (optional)</label>
                  <textarea rows={2} value={form.modifications} onChange={(e) => setForm({ ...form, modifications: e.target.value })} className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest resize-none" data-testid="movement-modifications" />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Video link (optional)</label>
                  <input type="text" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="YouTube link" className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest" data-testid="movement-video" />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Front photo {editing ? '(leave blank to keep current)' : '*'}</label>
                  {editing ? (
                    <p className="font-body text-xs text-stone-400">To change the photo, remove this movement and re-add it.</p>
                  ) : (
                    <input type="file" accept="image/*" required onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })} className="w-full font-body text-sm" data-testid="movement-file" />
                  )}
                </div>
                <button type="submit" disabled={saving || !form.title.trim()} className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-body font-medium px-5 py-3 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-50" data-testid="movement-submit">
                  {saving ? (<><Loader2 size={16} className="animate-spin" /> Saving…</>) : (editing ? 'Save changes' : 'Add movement')}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
